import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { adminService } from '../../services/adminService';
import './AdminSettings.css';

const AdminSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    warehousePincode: '',
    adminPrepDays: '',
    freeShippingOrderThreshold: '',
    defaultWeightGrams: '',
    boxLengthCm: '',
    boxWidthCm: '',
    boxHeightCm: '',
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getShippingConfig();
      const data = response.data || response;
      setConfig(data);
      setForm({
        warehousePincode: data.warehousePincode || '',
        adminPrepDays: data.adminPrepDays?.toString() || '',
        freeShippingOrderThreshold: data.freeShippingOrderThreshold?.toString() || '',
        defaultWeightGrams: data.defaultWeightGrams?.toString() || '',
        boxLengthCm: data.boxLengthCm?.toString() || '',
        boxWidthCm: data.boxWidthCm?.toString() || '',
        boxHeightCm: data.boxHeightCm?.toString() || '',
      });
    } catch (err) {
      console.error('Error fetching shipping config:', err);
      setError('Failed to load shipping settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        warehousePincode: form.warehousePincode.trim(),
        adminPrepDays: parseInt(form.adminPrepDays),
        freeShippingOrderThreshold: parseInt(form.freeShippingOrderThreshold),
        defaultWeightGrams: parseInt(form.defaultWeightGrams),
        boxLengthCm: parseFloat(form.boxLengthCm),
        boxWidthCm: parseFloat(form.boxWidthCm),
        boxHeightCm: parseFloat(form.boxHeightCm),
      };

      const response = await adminService.updateShippingConfig(payload);
      setConfig(response.data || response);
      setSuccess('Shipping settings updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating shipping config:', err);
      setError(err.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-settings"><div className="admin-loading">Loading...</div></div>;
  }

  return (
    <div className="admin-settings">
      {error && (
        <motion.div
          className="settings-alert settings-error"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {error}
        </motion.div>
      )}

      {success && (
        <motion.div
          className="settings-alert settings-success"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {success}
        </motion.div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="settings-card">
          <h3 className="settings-card-title">Shipping Configuration</h3>
          <p className="settings-card-desc">
            Configure warehouse, packaging, and free shipping rules. Changes take effect immediately (no redeployment needed).
          </p>

          <div className="settings-section">
            <h4 className="settings-section-title">Warehouse</h4>
            <div className="settings-row">
              <div className="settings-field">
                <label>Warehouse Pincode</label>
                <input
                  type="text"
                  value={form.warehousePincode}
                  onChange={(e) => setForm({ ...form, warehousePincode: e.target.value })}
                  placeholder="e.g. 204102"
                  maxLength={6}
                  required
                />
                <span className="field-hint">Origin pincode for shipping rate calculation</span>
              </div>
              <div className="settings-field">
                <label>Admin Prep Days</label>
                <input
                  type="number"
                  value={form.adminPrepDays}
                  onChange={(e) => setForm({ ...form, adminPrepDays: e.target.value })}
                  min="0"
                  max="30"
                  required
                />
                <span className="field-hint">Days added to delivery estimate for packaging</span>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h4 className="settings-section-title">Free Shipping</h4>
            <div className="settings-row">
              <div className="settings-field">
                <label>Minimum Items for Free Shipping</label>
                <input
                  type="number"
                  value={form.freeShippingOrderThreshold}
                  onChange={(e) => setForm({ ...form, freeShippingOrderThreshold: e.target.value })}
                  min="0"
                  required
                />
                <span className="field-hint">
                  If the user orders this many (or more) items in a single order, shipping is free. Set to 0 for free shipping on all orders.
                </span>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h4 className="settings-section-title">Package Dimensions</h4>
            <div className="settings-row">
              <div className="settings-field">
                <label>Default Weight (grams)</label>
                <input
                  type="number"
                  value={form.defaultWeightGrams}
                  onChange={(e) => setForm({ ...form, defaultWeightGrams: e.target.value })}
                  min="1"
                  required
                />
                <span className="field-hint">Weight sent to Delhivery for rate calculation</span>
              </div>
            </div>
            <div className="settings-row">
              <div className="settings-field">
                <label>Box Length (cm)</label>
                <input
                  type="number"
                  value={form.boxLengthCm}
                  onChange={(e) => setForm({ ...form, boxLengthCm: e.target.value })}
                  min="1"
                  step="0.1"
                  required
                />
              </div>
              <div className="settings-field">
                <label>Box Width (cm)</label>
                <input
                  type="number"
                  value={form.boxWidthCm}
                  onChange={(e) => setForm({ ...form, boxWidthCm: e.target.value })}
                  min="1"
                  step="0.1"
                  required
                />
              </div>
              <div className="settings-field">
                <label>Box Height (cm)</label>
                <input
                  type="number"
                  value={form.boxHeightCm}
                  onChange={(e) => setForm({ ...form, boxHeightCm: e.target.value })}
                  min="1"
                  step="0.1"
                  required
                />
              </div>
            </div>
            <span className="field-hint">
              Stored for reference. Current box: 10 x 10 x 12.5 inches = 25.4 x 25.4 x 31.75 cm
            </span>
          </div>

          <div className="settings-actions">
            <button type="submit" className="save-settings-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
